import React, { useCallback } from 'react';
import { Stack } from '@planne-software/uni/mui/material';
import { IconCheck, IconPhoto, IconPlus } from '@planne-software/uni/icons';
import { IconButton } from '@planne-software/uni/IconButton';
import { formatCents } from '@src/common';
import { useTranslate } from '@src/core/hooks';
import type { UpsellOffer } from '@src/core/buyerApiClient';
import {
  OfferRow,
  OwnerName,
  Price,
  PriceAndAction,
  PriceInfo,
  PricePrefix,
  ProductInfo,
  ProductName,
  ProductPhoto,
} from './styles';

type ProductUpsellRowProps = {
  offer: UpsellOffer;
  selected: boolean;
  onToggle: (productId: string) => void;
};

export const ProductUpsellRow = React.memo(function ProductUpsellRow(props: ProductUpsellRowProps) {
  const { offer, selected, onToggle } = props;
  const { i18n } = useTranslate();
  const product = offer.product;
  const priceCents = offer.priceInfo.minPrice.amountCents;
  const ownerName = product.app?.name ?? '';
  const handleToggle = useCallback(() => {
    onToggle(offer.productId);
  }, [offer.productId, onToggle]);

  return (
    <OfferRow>
      <ProductInfo>
        <ProductPhoto>
          {product?.thumbnailUrl ? <img src={product.thumbnailUrl} alt='' /> : <IconPhoto size={48} />}
        </ProductPhoto>

        <Stack minWidth={0}>
          <ProductName>{product.name}</ProductName>
          <OwnerName>{i18n('product_upsell_sold_by', { owner: ownerName })}</OwnerName>
        </Stack>
      </ProductInfo>

      <PriceAndAction>
        <PriceInfo>
          <PricePrefix>{i18n('product_upsell_price_prefix')}</PricePrefix>
          <Price>{formatCents(priceCents)}</Price>
        </PriceInfo>

        <IconButton
          color={selected ? 'primary' : 'neutral'}
          variant={selected ? 'contained' : 'outlined'}
          aria-pressed={selected}
          aria-label={i18n(selected ? 'product_upsell_remove_offer' : 'product_upsell_add_offer', {
            product: product.name,
          })}
          onClick={handleToggle}
        >
          {selected ? <IconCheck size={20} stroke={2.5} /> : <IconPlus size={20} stroke={2} />}
        </IconButton>
      </PriceAndAction>
    </OfferRow>
  );
});
